import { observer } from 'mobx-react-lite'

import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Divider from '@mui/material/Divider'
import Stack from '@mui/material/Stack'

import { Star } from '@mxenabled/mx-icons'

import { H3, Text } from '@mxenabled/mxui'

import {
  ToggleListItem,
  useEvent,
  useGlobalCopyStore,
  useSettingsStore,
  useUserStore,
} from '../../../common'
import { type UserCommunicationProfile } from '../../../core'

export const DeliveryMethods = observer(() => {
  const { onEvent } = useEvent()
  const { settings: copy } = useGlobalCopyStore()
  const {
    clientCommunicationProfile: ccp,
    updateUserCommunicationProfile,
    userCommunicationsProfile: ucp,
  } = useSettingsStore()
  const { user } = useUserStore()

  const onProfileChange = async <K extends keyof UserCommunicationProfile>(
    key: K,
    value: UserCommunicationProfile[K],
  ) => {
    const updatedProfile = { ...ucp, [key]: value }

    if (onEvent) {
      const keyParts = key.split('_')
      const notificationType = keyParts[3] // e.g., 'email', 'sms', 'push' 'all'
      if (value === false) {
        onEvent('opt_in_notification_method', { notificationType })
      } else {
        onEvent('opt_out_notification_method', { notificationType })
      }
    }

    await updateUserCommunicationProfile(updatedProfile)
  }

  return (
    <Stack>
      <H3 sx={{ mt: 24 }}>{copy.delivery_method_title}</H3>

      {ccp.email_notifications_enabled && (
        <>
          <ToggleListItem
            isChecked={!!user.email && !ucp.opted_out_of_email_notifications}
            isDisabled={!user.email}
            onToggle={() =>
              onProfileChange(
                'opted_out_of_email_notifications',
                !ucp.opted_out_of_email_notifications,
              )
            }
            primaryText={copy.delivery_method_email_title}
            secondaryText={
              !!user.email && !ucp.opted_out_of_email_notifications
                ? copy.notification_enabled
                : copy.notification_disabled
            }
            useExtraPadding={false}
          />

          {(ccp.sms_notifications_enabled || ccp.push_notifications_enabled) && <Divider />}
        </>
      )}

      {ccp.sms_notifications_enabled && (
        <>
          <ToggleListItem
            isChecked={!!user.phone && !ucp.opted_out_of_sms_notifications}
            isDisabled={!user.phone}
            onToggle={() =>
              onProfileChange('opted_out_of_sms_notifications', !ucp.opted_out_of_sms_notifications)
            }
            primaryText={copy.delivery_method_sms_title}
            secondaryText={
              !!user.phone && !ucp.opted_out_of_sms_notifications
                ? copy.notification_enabled
                : copy.notification_disabled
            }
            useExtraPadding={false}
          />

          {ccp.push_notifications_enabled && <Divider />}
        </>
      )}

      {ccp.push_notifications_enabled && (
        <ToggleListItem
          isChecked={!ucp.opted_out_of_push_notifications}
          isDisabled={true}
          onToggle={() => {}}
          primaryText={copy.delivery_method_push_title}
          secondaryText={copy.delivery_method_push_subtitle}
          useExtraPadding={false}
        />
      )}

      {ccp.sms_notifications_enabled && (
        <Card sx={{ backgroundColor: 'background.default', mt: 12 }} variant="outlined">
          <CardContent>
            <Stack alignItems="flex-start" direction="row" gap={6}>
              <Star color="primary" filled={true} />
              <Text variant="caption">{copy.delivery_method_sms_note_1}</Text>
            </Stack>
          </CardContent>
        </Card>
      )}
    </Stack>
  )
})
